import { Component, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
  section?: string;
  compact?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Section-level error boundary — prevents one broken section
 * from crashing the entire page.
 */
export class SectionErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(
      `[SectionErrorBoundary${this.props.section ? ` - ${this.props.section}` : ""}]`,
      error,
      info.componentStack
    );
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.compact) {
      return (
        <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-4 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-destructive shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">
              Erro{this.props.section ? ` em ${this.props.section}` : ""}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {this.state.error?.message || "Erro inesperado"}
            </p>
          </div>
          <Button size="sm" variant="ghost" onClick={this.handleReset} className="shrink-0">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      );
    }

    return (
      <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-6 text-center space-y-3">
        <div className="h-10 w-10 rounded-xl bg-destructive/10 flex items-center justify-center mx-auto">
          <AlertTriangle className="h-5 w-5 text-destructive" />
        </div>
        <p className="text-sm font-semibold text-foreground">
          Erro{this.props.section ? ` em ${this.props.section}` : ""}
        </p>
        <p className="text-xs text-muted-foreground">
          {this.state.error?.message || "Ocorreu um erro inesperado nesta seção."}
        </p>
        <Button size="sm" variant="outline" onClick={this.handleReset} className="gap-1.5">
          <RefreshCw className="h-3.5 w-3.5" /> Tentar novamente
        </Button>
      </div>
    );
  }
}
